import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const googleCalendarApi = createApi({
  reducerPath: "googleCalendarApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["GoogleCalendarEvents", "GoogleCalendarConnection"],
  endpoints: (builder) => ({
    // Check connection status
    checkGoogleCalendarConnection: builder.query({
      query: () => ({
        url: "/google-calendar/connection-status",
        method: "GET",
      }),
      providesTags: ["GoogleCalendarConnection"],
      transformResponse: (response) => response.data || { connected: false },
    }),

    // Get auth URL for connecting
    getGoogleCalendarAuthUrl: builder.query({
      query: () => ({
        url: "/google-calendar/auth-url",
        method: "GET",
      }),
      transformResponse: (response) => response.data?.authUrl,
    }),

    // List events
    listGoogleCalendarEvents: builder.query({
      query: ({ timeMin, timeMax, maxResults, calendarId } = {}) => ({
        url: "/google-calendar/events",
        method: "GET",
        params: { timeMin, timeMax, maxResults, calendarId },
      }),
      providesTags: ["GoogleCalendarEvents"],
      transformResponse: (response) => response.data || [],
    }),

    // Get single event
    getGoogleCalendarEvent: builder.query({
      query: ({ eventId, calendarId }) => ({
        url: `/google-calendar/events/${eventId}`,
        method: "GET",
        params: { calendarId },
      }),
      transformResponse: (response) => response.data,
    }),

    // Create event
    createGoogleCalendarEvent: builder.mutation({
      query: (eventData) => ({
        url: "/google-calendar/events",
        method: "POST",
        body: eventData,
      }),
      invalidatesTags: ["GoogleCalendarEvents"],
    }),

    // Update event
    updateGoogleCalendarEvent: builder.mutation({
      query: ({ eventId, ...eventData }) => ({
        url: `/google-calendar/events/${eventId}`,
        method: "PUT",
        body: eventData,
      }),
      invalidatesTags: ["GoogleCalendarEvents"],
    }),

    // Delete event
    deleteGoogleCalendarEvent: builder.mutation({
      query: ({ eventId, calendarId }) => ({
        url: `/google-calendar/events/${eventId}`,
        method: "DELETE",
        params: { calendarId },
      }),
      invalidatesTags: ["GoogleCalendarEvents"],
    }),

    // List user's calendars
    listGoogleCalendars: builder.query({
      query: () => ({
        url: "/google-calendar/calendars",
        method: "GET",
      }),
      transformResponse: (response) => response.data || [],
    }),

    // Get calendar colors
    getGoogleCalendarColors: builder.query({
      query: () => ({
        url: "/google-calendar/colors",
        method: "GET",
      }),
      transformResponse: (response) => response.data,
    }),
  }),
});

export const {
  useCheckGoogleCalendarConnectionQuery,
  useLazyGetGoogleCalendarAuthUrlQuery,
  useListGoogleCalendarEventsQuery,
  useGetGoogleCalendarEventQuery,
  useCreateGoogleCalendarEventMutation,
  useUpdateGoogleCalendarEventMutation,
  useDeleteGoogleCalendarEventMutation,
  useListGoogleCalendarsQuery,
  useGetGoogleCalendarColorsQuery,
} = googleCalendarApi;
